// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState } from 'react'
import { Eye, EyeOff, Lock, RefreshCw, Copy, CheckCircle2 } from 'lucide-react'
import { Input } from '@/components/ui'
import { DEMO_MODE } from '@/config/demo'
import { cn, checkPasswordStrength } from '@/lib/utils'
import type { OnboardingData } from '@/types'

// ── Wachtwoordgeneratie (productie + voorbeeld in demo) ────────────────────────

const CHARS = {
  upper:   'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lower:   'abcdefghjkmnpqrstuvwxyz',
  digits:  '23456789',
  special: '!@#$%^&*-_=+?',
}
const ALL    = CHARS.upper + CHARS.lower + CHARS.digits + CHARS.special
const LENGTH = 16

function generateSecurePassword(): string {
  const required = [
    CHARS.upper[   Math.floor(Math.random() * CHARS.upper.length)   ],
    CHARS.lower[   Math.floor(Math.random() * CHARS.lower.length)   ],
    CHARS.digits[  Math.floor(Math.random() * CHARS.digits.length)  ],
    CHARS.special[ Math.floor(Math.random() * CHARS.special.length) ],
  ]
  const randomBytes = new Uint32Array(LENGTH - required.length)
  crypto.getRandomValues(randomBytes)
  const rest = Array.from(randomBytes, n => ALL[n % ALL.length])
  const all  = [...required, ...rest]
  const shuffleBytes = new Uint32Array(all.length)
  crypto.getRandomValues(shuffleBytes)
  return all
    .map((char, i) => ({ char, sort: shuffleBytes[i] }))
    .sort((a, b) => a.sort - b.sort)
    .map(x => x.char)
    .join('')
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Step8PasswordProps {
  data:     Partial<OnboardingData>
  onChange: (updates: Partial<OnboardingData>) => void
  errors:   Partial<Record<keyof OnboardingData, string>>
}

// ── DEMO-scherm ────────────────────────────────────────────────────────────────

function DemoPasswordScreen() {
  const [example, setExample]   = useState(() => generateSecurePassword())
  const [copied,  setCopied]    = useState(false)

  function handleNewExample() {
    setExample(generateSecurePassword())
    setCopied(false)
  }

  function handleCopyExample() {
    navigator.clipboard.writeText(example).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold text-ink mb-1">Wachtwoord</h2>
        <p className="text-sm text-ink-3">
          In de demo-versie wordt een vast wachtwoord gebruikt.
        </p>
      </div>

      {/* Actief demo-wachtwoord */}
      <div className="rounded-xl border-2 border-sage-400 bg-sage-50 px-5 py-4">
        <p className="text-xs font-semibold text-sage-700 uppercase tracking-wider mb-2">
          Demonstratiewachtwoord (actief)
        </p>
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold text-sage-900">
            Vooraf ingesteld beveiligd demonstratiewachtwoord
          </p>
          <div className="flex items-center gap-1.5 rounded-lg bg-sage-200 px-2.5 py-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-sage-700" />
            <span className="text-xs font-semibold text-sage-700">Actief</span>
          </div>
        </div>
        <p className="text-xs text-sage-600 mt-2">
          Voor inloggen gebruikt u het vooraf ingestelde demonstratiewachtwoord.
        </p>
      </div>

      {/* Voorbeeld sterk wachtwoord */}
      <div className="rounded-xl border border-sand-300 bg-white px-5 py-4">
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-3">
          Voorbeeld sterk wachtwoord
        </p>
        <div className="flex items-center gap-2 mb-3">
          <p className="flex-1 text-sm font-mono text-ink bg-sand-100 rounded-lg px-3 py-2 tracking-widest break-all">
            {example}
          </p>
          <button
            type="button"
            onClick={handleCopyExample}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-sand-300 text-ink-4 hover:text-sage-600 hover:border-sage-300 transition-colors flex-shrink-0"
            title="Kopieer voorbeeld"
          >
            {copied
              ? <CheckCircle2 className="h-4 w-4 text-sage-500" />
              : <Copy         className="h-4 w-4" />
            }
          </button>
        </div>
        {copied && (
          <p className="text-xs text-sage-600 mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Voorbeeld gekopieerd
          </p>
        )}
        <button
          type="button"
          onClick={handleNewExample}
          className="flex items-center gap-2 text-xs font-semibold text-ink-4 hover:text-ink-3 transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Genereer nieuw voorbeeld
        </button>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 rounded-xl bg-status-warnBg border border-status-warnBorder px-4 py-3">
        <span className="text-sm flex-shrink-0">ℹ️</span>
        <p className="text-xs text-status-warnText leading-relaxed">
          Dit voorbeeld wordt uitsluitend getoond ter demonstratie.
          Dit voorbeeld is alleen ter demonstratie en wordt nooit opgeslagen of gebruikt voor encryptie.
          Het actieve demo-wachtwoord is vooraf ingesteld.
        </p>
      </div>

      {/* Info */}
      <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-100 px-4 py-3.5">
        <Lock className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-sage-700">In productie kiest u zelf een sterk wachtwoord</p>
          <p className="text-xs text-sage-600/80 mt-0.5 leading-relaxed">
            Uw gegevens worden versleuteld met uw wachtwoord. WilsApp slaat
            het wachtwoord nergens op en kan het niet herstellen.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Productie-scherm ───────────────────────────────────────────────────────────

function ProductionPasswordScreen({ data, onChange, errors }: Step8PasswordProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm,  setShowConfirm]  = useState(false)
  const [copied,       setCopied]       = useState(false)

  const password = data.password ?? ''
  const strength = password ? checkPasswordStrength(password) : null

  const strengthLabels = ['', 'Te zwak', 'Zwak', 'Redelijk', 'Goed', 'Sterk']
  const strengthColors = ['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-sage-400', 'bg-sage-600']

  function handleGenerate() {
    const pw = generateSecurePassword()
    onChange({ password: pw, passwordConfirm: pw })
    setShowPassword(true)
    setShowConfirm(true)
    setCopied(false)
  }

  function handleCopy() {
    if (!password) return
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold text-ink mb-1">Kies een wachtwoord</h2>
        <p className="text-sm text-ink-3">Uw wachtwoord versleutelt uw gegevens op dit apparaat.</p>
      </div>

      <button type="button" onClick={handleGenerate}
        className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sage-300 bg-sage-50 px-4 py-3 text-sm font-semibold text-sage-700 hover:border-sage-500 hover:bg-sage-100 active:scale-[0.98] transition-all">
        <RefreshCw className="h-4 w-4" />
        Sterk wachtwoord genereren
      </button>

      <div className="flex flex-col gap-1.5">
        <div className="relative">
          <Input label="Wachtwoord" type={showPassword ? 'text' : 'password'} value={password}
            onChange={(e) => onChange({ password: e.target.value })}
            error={errors.password} autoComplete="new-password" required />
          <div className="absolute right-3.5 top-[38px] flex items-center gap-1">
            {password && (
              <button type="button" tabIndex={-1} onClick={handleCopy}
                className="text-ink-4 hover:text-sage-600 transition-colors p-0.5">
                {copied ? <CheckCircle2 className="h-4 w-4 text-sage-500" /> : <Copy className="h-4 w-4" />}
              </button>
            )}
            <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)}
              className="text-ink-4 hover:text-ink-3 transition-colors p-0.5">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {strength && (
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-1">
              {[1,2,3,4].map(i => (
                <div key={i} className={cn('flex-1 h-1.5 rounded-full transition-all duration-300',
                  i <= strength.score ? strengthColors[strength.score] : 'bg-sand-300')} />
              ))}
            </div>
            <p className={cn('text-xs font-medium',
              strength.score <= 1 ? 'text-red-500' : strength.score <= 2 ? 'text-orange-500' :
              strength.score <= 3 ? 'text-yellow-600' : 'text-sage-600')}>
              Sterkte: {strengthLabels[strength.score]}
            </p>
          </div>
        )}
        {copied && (
          <p className="text-xs text-sage-600 font-medium flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />Gekopieerd naar klembord
          </p>
        )}
      </div>

      <div className="relative">
        <Input label="Wachtwoord bevestigen" type={showConfirm ? 'text' : 'password'}
          value={data.passwordConfirm ?? ''}
          onChange={(e) => onChange({ passwordConfirm: e.target.value })}
          error={errors.passwordConfirm} autoComplete="new-password" required />
        <button type="button" tabIndex={-1} onClick={() => setShowConfirm(v => !v)}
          className="absolute right-3.5 top-[38px] text-ink-4 hover:text-ink-3 transition-colors">
          {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-100 px-4 py-3.5">
        <Lock className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-sage-700">Uw wachtwoord is uw sleutel</p>
          <p className="text-xs text-sage-600/80 mt-0.5 leading-relaxed">
            Uw gegevens worden met dit wachtwoord versleuteld. WilsApp heeft
            geen toegang tot uw wachtwoord en kan het niet herstellen als u het vergeet.{' '}
            <strong>Schrijf het op en bewaar het op een veilige plek.</strong>
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Exporteer juiste scherm op basis van DEMO_MODE ─────────────────────────────

export function Step8Password(props: Step8PasswordProps) {
  if (DEMO_MODE) return <DemoPasswordScreen />
  return <ProductionPasswordScreen {...props} />
}
